// src/pages/admin/MarketsPanel.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import { listMarkets, createMarket, updateMarket } from "../../api/adminService";

const STATUS_OPTIONS = ["open", "full", "closed"];

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
];

const statusChipColor = (status) => {
  switch (status) {
    case "open": return "success";
    case "full": return "warning";
    case "closed": return "error";
    default: return "default";
  }
};

export default function MarketsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ city: "", state: "", capacity: 10, status: "open" });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ capacity: 10, status: "open" });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listMarkets();
      setRows(Array.isArray(data) ? data : data.markets || data.items || []);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateOpen = () => {
    setCreateForm({ city: "", state: "", capacity: 10, status: "open" });
    setCreateError("");
    setCreateOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!createForm.city.trim() || !createForm.state.trim()) {
      setCreateError("City and state are required.");
      return;
    }
    setCreateLoading(true);
    setCreateError("");
    try {
      await createMarket(createForm);
      setCreateOpen(false);
      fetchData();
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditOpen = (row) => {
    setEditTarget(row);
    setEditForm({ capacity: row.capacity, status: row.status });
    setEditError("");
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    setEditError("");
    try {
      await updateMarket(editTarget.id, editForm);
      setEditOpen(false);
      fetchData();
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Markets
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={handleCreateOpen}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Add Market
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>City</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Capacity</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Usage</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No markets configured.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const current = row.current_active_members ?? 0;
                const capacity = row.capacity ?? 1;
                const pct = Math.min((current / capacity) * 100, 100);

                return (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.city}</TableCell>
                    <TableCell>{row.state}</TableCell>
                    <TableCell>{capacity}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "grey.200",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 4,
                              bgcolor: pct >= 90 ? "error.main" : pct >= 70 ? "warning.main" : "success.main",
                            },
                          }}
                        />
                        <Typography variant="caption" fontWeight={600} sx={{ minWidth: 50 }}>
                          {current}/{capacity}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={statusChipColor(row.status)}
                        size="small"
                        sx={{ fontWeight: 600, textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<EditRoundedIcon />}
                        onClick={() => handleEditOpen(row)}
                        sx={{ textTransform: "none", fontWeight: 600, fontSize: 12 }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Market Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Market</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <TextField
            label="City"
            value={createForm.city}
            onChange={(e) => setCreateForm((p) => ({ ...p, city: e.target.value }))}
            fullWidth
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            select
            label="State"
            value={createForm.state}
            onChange={(e) => setCreateForm((p) => ({ ...p, state: e.target.value }))}
            fullWidth
            required
            sx={{ mb: 2 }}
          >
            {US_STATES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Capacity"
            type="number"
            value={createForm.capacity}
            onChange={(e) => setCreateForm((p) => ({ ...p, capacity: parseInt(e.target.value, 10) || 10 }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label="Status"
            value={createForm.status}
            onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value }))}
            fullWidth
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={createLoading}
          >
            {createLoading ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Market Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Market{editTarget ? ` — ${editTarget.city}, ${editTarget.state}` : ""}
        </DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <TextField
            label="Capacity"
            type="number"
            value={editForm.capacity}
            onChange={(e) => setEditForm((p) => ({ ...p, capacity: parseInt(e.target.value, 10) || 1 }))}
            fullWidth
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            select
            label="Status"
            value={editForm.status}
            onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
            fullWidth
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
